import type { AnyOrama, Orama, SearchParams } from '@orama/orama'
import type { OramaClient, SearchMode } from '@oramacloud/client'
import type { ThemeConfig } from './defaultConfig'
import type { DocsDocument, ReturningProps, SeeAllLinkProps } from './types'

import { computed, useSignal } from '@preact/signals-react'
import r2wc from '@r2wc/react-to-web-component'
import clx from 'classnames'
import { toCustomPropertiesString } from 'object-to-css-variables'
import { useEffect, useRef } from 'react'

import { useSignals } from '@preact/signals-react/runtime'
import { Footer } from './components/Footer/Footer'
import { SearchInput } from './components/SearchInput/SearchInput'
import { SearchResults } from './components/SearchResults/SearchResults'
import { ShowSummaryCTA } from './components/ShowSummaryCTA/ShowSummaryCTA'
import { Summary } from './components/Summary/Summary'
import { useClickOutside } from './hooks/useClickOutside'
import * as $ from './signal/searchbox'
import globalStyles from './styles/global.module.css'
import styles from './styles/searchbox.module.css'
import { checkParams } from './utils/params'

export interface RegisterSearchBoxParams {
  oramaInstance?: AnyOrama | OramaClient
  showFullscreenOnMobile?: boolean
  colorScheme?: string
  themeConfig?: ThemeConfig
  searchInputPlaceholder?: string
  searchResultsPlaceholder?: string
  noResultsTemplate?: string
  show?: boolean
  backdrop?: boolean
  container?: string
  resultsMap?: Partial<ReturningProps>
  showShowMoreLink?: boolean
  showResultIcon?: boolean
  searchMode?: SearchMode
  keyboardNavigationHints?: boolean
  facetProperty?: string
  seeAllLink?: SeeAllLinkProps
  searchParams?: SearchParams<Orama<AnyOrama | OramaClient>>
  cloudConfig?: {
    url: string
    key: string
  }
  keepOpen?: boolean
  onClose?: () => void
  setResultTitle?: (doc: DocsDocument) => string
  renderItem?: (document: DocsDocument) => JSX.Element
  setResultLink?: (document: DocsDocument) => string
  theme?: 'primary' | 'secondary'
}

export function SearchBox(props: RegisterSearchBoxParams) {
  useSignals()

  useEffect(() => {
    checkParams(props)
  }, [props])

  const client = useSignal($.oramaClient).value
  const colorScheme = useSignal($.colorScheme).value
  const results = useSignal($.searchResults).value
  const focusedResult = useSignal($.focusedResultIndex).value
  const shouldShowSearchbox = useSignal($.show).value
  const theme = useSignal($.theme).value
  const summaryExpanded = useSignal($.isSummaryExpanded).value
  const keepOpen = useSignal($.keepOpen).value
  const searchboxRef = useRef<HTMLDivElement>(null)

  useClickOutside(searchboxRef, () => {
    if (keepOpen.value) return
    $.closeAndReset()
  })

  const themeStyle = toCustomPropertiesString($.themeConfig.value[colorScheme.value])

  const style = (
    <style>
      {`
      @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap');
      #orama-searchbox {
        ${themeStyle}
        '--font-primary': '"Instrument Sans", sans-serif',
        font-size: 100%;
      }
    `}
    </style>
  )

  const showSearchInput = computed(
    () => theme.value === 'primary' || (theme.value === 'secondary' && !summaryExpanded.value)
  )

  useEffect(() => {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (!keepOpen.value) $.closeAndReset()

        const event = new CustomEvent('orama-wc-searchbox:close', {
          bubbles: true
        })
        document.dispatchEvent(event)
      }

      if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        e.stopPropagation()
        $.show.value = true
      }
    })

    document.addEventListener('keydown', trapTabKey)

    document.addEventListener('orama-wc-searchbutton:click', () => {
      $.show.value = true
    })

    return () => {
      document.removeEventListener('keydown', () => {})
      document.removeEventListener('orama-wc-searchbutton:click', () => {})
    }
  }, [])

  useEffect(() => {
    if (shouldShowSearchbox.value) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [shouldShowSearchbox.value])

  const handleKeyboardCommands = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let nextCount = 0
    const items =
      $.currentFacet.value === 'all'
        ? results.value?.hits
        : results.value?.groupedResults?.find((group) => group[0] === $.currentFacet.value)?.[1]

    const hits = items ? items.length : 0

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      nextCount = (focusedResult.value + 1) % (hits + 1) === 0 ? 1 : (focusedResult.value + 1) % (hits + 1)
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()

      nextCount = (focusedResult.value + hits) % (hits + 1) === 0 ? hits : (focusedResult.value + hits) % (hits + 1)
    }
    if (e.key === 'Enter') {
      const currentItem = searchboxRef.current?.querySelector('[data-current-item="true"]')

      if (currentItem !== null) {
        ;(currentItem as HTMLAnchorElement).click()
      }
    }

    $.focusedResultIndex.value = nextCount
  }

  function trapTabKey(e: KeyboardEvent) {
    const focusableElements = searchboxRef.current?.querySelectorAll('button, [href], input, select, textarea')

    if (!focusableElements || focusableElements.length === 0) return

    const focusableElementsArray = Array.from(focusableElements).filter(
      (el) => el.getAttribute('tabindex') !== '-1' && el.getAttribute('aria-hidden') !== 'true'
    )

    const firstElement = focusableElementsArray[0] as HTMLElement
    const lastElement = focusableElementsArray[focusableElementsArray.length - 2] as HTMLElement

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          ;(lastElement as HTMLElement).focus()
          e.preventDefault()
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus()
          e.preventDefault()
        }
      }
    }
  }

  if (!shouldShowSearchbox.value) return null

  if (!client.value) {
    return (
      <div className={styles.searchbox_outer}>
        {style}
        <div>Loading Searchbox...</div>
      </div>
    )
  }

  return (
    <div
      id="orama-searchbox"
      role="dialog"
      aria-labelledby="dialogTitle"
      aria-modal="true"
      className={styles[`searchbox--${theme.value}`]}
    >
      {style}
      <div className={styles.searchbox_container}>
        {$.backdrop.value && <div className={styles.backdrop} />}
        <div
          className={clx(styles.searchbox, globalStyles.customScrollbar, styles[`searchbox--${theme.value}`])}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => handleKeyboardCommands(e)}
          ref={searchboxRef}
          role="document"
        >
          <h1 id="dialogTitle" className={globalStyles['sr-only']}>
            {$.searchInputPlaceholder.value}
          </h1>
          {showSearchInput.value && <SearchInput />}
          <SearchResults />
          <ShowSummaryCTA show={!$.isSummaryExpanded.value} />
          <Summary />
          <Footer />
        </div>
      </div>
    </div>
  )
}

export function RegisterSearchBox(params: RegisterSearchBoxParams) {
  checkParams(params)
  if (!customElements.get('orama-searchbox')) {
    // biome-ignore lint/suspicious/noExplicitAny: any is fine here
    const el = customElements.define('orama-searchbox', r2wc(SearchBox as any))
    $.ready.value = true
    return el
  }

  $.ready.value = true
}
